import type { ReactElement } from 'react';
import { Check } from 'lucide-react';
import { formatCurrency } from '@xdocs/i18n';
import { Badge, Button, Input, cn, useI18n } from '@xdocs/ui';
import { safeT } from '../../../i18n/safeT.js';
import type { UsePromoRedeemResult } from './usePromoRedeem.js';
import { useSettingsBillingT } from './useSettingsBillingT.js';
import styles from './PromoCodeField.module.css';

export interface PromoCodeFieldProps {
  promo: UsePromoRedeemResult;
}

/** F56 promo-code field — shared by the F24 upgrade flow and the F57
 * buy-credits flow. States: empty / validating / applied / invalid. */
export function PromoCodeField({ promo }: PromoCodeFieldProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsBillingT();

  if (promo.status === 'applied' && promo.effect) {
    return (
      <div className={cn(styles['appliedRow'])}>
        <Badge tone="success" icon={<Check size={11} aria-hidden="true" />}>
          {describeEffect(promo.effect, locale, t)}
        </Badge>
        <Button variant="ghost" size="sm" onClick={promo.remove}>
          {t('settingsBilling:promo.remove')}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn(styles['row'])}>
      <Input
        label={t('settingsBilling:promo.label')}
        placeholder={t('settingsBilling:promo.placeholder')}
        value={promo.code}
        onChange={(event) => promo.setCode(event.target.value)}
        loading={promo.status === 'validating'}
        {...(promo.status === 'invalid' && promo.errorMessage ? { error: safeT(locale, promo.errorMessage) } : {})}
        containerClassName={cn(styles['input'])}
      />
      <Button
        variant="secondary"
        state={promo.status === 'validating' ? 'loading' : promo.status === 'invalid' ? 'error' : 'idle'}
        onClick={() => void promo.apply()}
      >
        {t('settingsBilling:promo.apply')}
      </Button>
    </div>
  );
}

function describeEffect(effect: NonNullable<UsePromoRedeemResult['effect']>, locale: ReturnType<typeof useI18n>['locale'], t: ReturnType<typeof useSettingsBillingT>['t']): ReactElement | string {
  if (effect.kind === 'percent_off') return t('settingsBilling:promo.applied.percent', { percent: effect.percent });
  if (effect.kind === 'fixed_off') return t('settingsBilling:promo.applied.fixed', { amount: formatCurrency(locale, effect.amount, effect.currency) });
  return t('settingsBilling:promo.applied.bonus', { count: effect.credits });
}
